<template>
  <div>
    <vl-grid
      :class="{
        edit: editStore.isEdit(EntityModelEditType.CodeListAttributeMapping, id),
        'vl-u-spacer-bottom--medium': true
      }"
    >
      <vl-column width="8" width-xs="12">
        <vl-title tag-name="h2"> {{ $t('codeListAttributeMapping.detail.title') }} </vl-title>
      </vl-column>
      <vl-column width="4" width-xs="12">
        <vl-action-group
          v-if="!editStore.isEdit(EntityModelEditType.CodeListAttributeMapping, id)"
          mod-align-right
          mod-space
          between
          ><vl-button
            v-if="editStore.canEdit && !hasMapping"
            icon="pencil"
            mod-icon-before
            mod-primary
            :mod-disabled="editStore.editing"
            @click="editStore.openEdit(EntityModelEditType.CodeListAttributeMapping, id)"
          >
            {{ $t('general.add') }}
          </vl-button>
          <vl-button
            v-if="editStore.canEdit && hasMapping"
            icon="pencil"
            mod-icon-before
            mod-secondary
            :mod-disabled="editStore.editing"
            @click="editStore.openEdit(EntityModelEditType.CodeListAttributeMapping, id)"
          >
            {{ $t('general.edit') }}
          </vl-button>
          <vl-button
            v-if="editStore.canEdit && hasMapping"
            icon="trash"
            mod-icon-before
            mod-error
            @click="showDeleteModal()"
            >{{ $t('general.delete') }}</vl-button
          >
        </vl-action-group>
        <vl-action-group
          v-if="editStore.isEdit(EntityModelEditType.CodeListAttributeMapping, id)"
          mod-align-right
          mod-space
          between
        >
          <vl-button icon="save" :mod-loading="reloading" mod-icon-before @click="save">{{
            $t('general.save')
          }}</vl-button>
          <vl-button
            icon="cross"
            :mod-loading="loading"
            mod-icon-before
            mod-secondary
            mod-error
            @click="editStore.closeEdit"
          >
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>

      <vl-column width="12" width-xs="12">
        <template v-if="editStore.isEdit(EntityModelEditType.CodeListAttributeMapping, id)">
          <CodeListAttributeMappingEdit ref="editForm" :detail="detail" :model="model" />
        </template>
        <template v-else-if="hasMapping">
          <CodeListAttributeMappingRead :detail="detail" />
        </template>
        <template v-else>
          <vl-annotation>{{ $t('codeListAttributeMapping.detail.noResults') }}</vl-annotation>
        </template>
      </vl-column>
    </vl-grid>
  </div>
</template>
<script lang="ts" setup>
import { CodeListAttributeMappingDto, EntityModelDetailDto } from '@/api/backoffice/clients';
import { computed, ref, Ref, onMounted } from 'vue';
import { useCodeListAttributeMappingDetailStore } from '@/modules/configuration/store/code-list-attribute-mapping/code-list-attribute-mapping-detail.store';
import {
  useEntityModelEditStore,
  EntityModelEditType
} from '@/modules/configuration/store/entity-model/entity-model-edit.store';
import { useRoute } from 'vue-router';
import { useModalStore } from '@/modules/core/store/modal.store';
import CodeListAttributeMappingRead from './CodeListAttributeMappingRead.vue';
import CodeListAttributeMappingEdit from './CodeListAttributeMappingEdit.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const store = useCodeListAttributeMappingDetailStore();
const editStore = useEntityModelEditStore();
const modalStore = useModalStore();

const route = useRoute();

const editForm: Ref<typeof CodeListAttributeMappingEdit> = ref(null);

defineProps<{
  model: EntityModelDetailDto;
}>();

const id = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

const detail = computed((): CodeListAttributeMappingDto => {
  return store.detail || {};
});

const hasMapping = computed((): boolean => {
  const mappings = detail.value.attributeMappings;
  return mappings ? Object.values(mappings).length > 0 : false;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const loading = computed((): boolean => {
  return store.loading;
});

const save = () => {
  editForm.value.onSubmit();
};

const showDeleteModal = () => {
  modalStore.showDeleteModal({
    title: t('codeListAttributeMapping.detail.delete.title'),
    props: {
      confirm: () => store.remove(id.value)
    }
  });
};

onMounted(async () => {
  await store.get(id.value);
});
</script>
<style lang="scss" scoped></style>
